<script lang="ts">
							import SectionComponent from '$lib/section/SectionComponent.svelte';
							import copy from "copy-to-clipboard"; 
							import { onMount } from "svelte";
							import PrevNextSection from '$lib/prevNext/PrevNextSection.svelte';

							interface ComponentProps {
								setVisible: (id: string, v: boolean) => void;
							}
							let { setVisible }: ComponentProps = $props();
							
							const visibleById = $state<Record<string, boolean>>({});

							/**
               * This function will go through all the 'pre' elements
               * on the page and add a copy button to them.
               * Thanks to: https://slavbasharov.com/blog/adding-click-to-copy-code-markdown-blog
               */
              onMount(() => {
                  const codeBlocks = document.querySelectorAll("pre");
                  codeBlocks.forEach((block) => {
                      const copyPrompt = document.createElement("div");
                      copyPrompt.className = "copy-prompt";
                      const copyPromptText = document.createElement("p");
                      copyPromptText.innerHTML = "👆 Click to copy";
                      copyPromptText.className = 'copy-prompt-p';
                      const copyIcon = document.createElement("img");
                      copyIcon.src = "/icons/copy-icon.svg";
                      copyIcon.className = "copy-prompt-img";
                      copyPrompt.appendChild(copyIcon);
                      copyPrompt.appendChild(copyPromptText);
                      block.appendChild(copyPrompt);
											block.querySelector('.copy-prompt > p')?.addEventListener('click', () => {
												if (block.querySelector('code')?.textContent !== null && block.querySelector('code')?.textContent !== undefined) {
													copy(block.querySelector('code')!.textContent!);
												}
												if (block.querySelector('.copy-prompt > p')?.innerHTML !== null && block.querySelector('.copy-prompt > p')?.innerHTML !== undefined) {
													block.querySelector('.copy-prompt > p')!.innerHTML = 'Copied!';
													setTimeout(() => {
														block.querySelector('.copy-prompt > p')!.innerHTML = '👆 Click to copy';
													}, 1000);
												}
											});
                  });
              });
		let prevLink= '/Examples/External_Components/A_Sorted_Table';
    let nextLink= '/Examples/Website_Integration/Getting_Language_Information';
    
    import Note from "$lib/notes/Note.svelte";
    import Figure from "$lib/figures/Figure.svelte";
</script>

<PrevNextSection {prevLink} {nextLink} />



<SectionComponent tag="h1" 
  id="integrating-a-freon-editor-into-your-own-web-app-1"
  bind:intersecting={visibleById["integrating-a-freon-editor-into-your-own-web-app-1"]}
  onIntersectChange={(visible) => setVisible("integrating-a-freon-editor-into-your-own-web-app-1", visible)}
>Integrating a Freon Editor into your own Web App</SectionComponent>
<p>As explained in <a
  href="/Documentation/Overview/Getting_Started#a-minimal-web-app-and-server-6"
>Getting Started</a>, the web
application that Freon provides will usually need to be replaced with appropriate solutions for the
company or organization where the resulting product will be used. In this example we explain how to integrate a Freon
editor in your own web app. The code that is shown here can be found in
a GitHub repository called <a href="https://github.com/freon4dsl/IntegrationExample" target="_blank">IntegrationExample</a>.</p>
<p>Note, this example does not extend to a complete web app. Many aspects that are necessary in a proper product
still need to be implemented. Because these aspects highly depend on the exact look-and-feel and functioning
of the web app, the variation will be extreme. Therefore, we leave these aspects as ‘an exercise’. The aim of
the example (and the GitHub repo that accompanies it) is to give you some pointers on how and where to use
functionality from the Freon <code>core</code> and <code>core-svelte</code> packages.</p>
<SectionComponent tag="h2" 
  id="mono-repository-2"
  bind:intersecting={visibleById["mono-repository-2"]}
  onIntersectChange={(visible) => setVisible("mono-repository-2", visible)}
>Mono Repository</SectionComponent>
<p>In the <a href="https://github.com/freon4dsl/IntegrationExample" target="_blank">IntegrationExample</a> repository we have combined two projects into one mono repository, a Freon language
project in the package <code>dsl</code>, and a <a href="https://flowbite-svelte.com/" target="_blank">Flowbite</a> web app project in
the package <code>webapp</code>. To make everything working in a mono repo slight changes were made to the <code>package.json</code>
files, but in essence you can start with the ‘Getting Started’ projects from both sources.</p>
<p>Of course, you can also choose to use other UI and CSS libraries.</p>
<SectionComponent tag="h2" 
  id="baseline-web-app-3"
  bind:intersecting={visibleById["baseline-web-app-3"]}
  onIntersectChange={(visible) => setVisible("baseline-web-app-3", visible)}
>Baseline Web App</SectionComponent>
<p>In the <code>webapp</code> package of our mono repo we have created a Flowbite web page with some initially empty places where
the Freon editor, and other information, for instance about the language, should be added.
This web app resembles the one that comes with Freon, which was built
using a different Svelte library called <a href="https://sveltematerialui.com" target="_blank">Svelte Material UI</a>, and a different CSS library (Sass).</p>
<p>If you want to dive into the code in our <a href="https://github.com/freon4dsl/IntegrationExample" target="_blank">repository</a>, take a look at the
pull requests. We have tried to keep the example as clean as possible, such that almost every pull request represents a step in
this tutorial.</p>


<PrevNextSection {prevLink} {nextLink} />